'use client';

import Link from 'next/link';
import { ChevronLeft } from 'lucide-react';

export default function PlatformTermsPage() {
  return (
    <div className="bg-white">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-100 py-4">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-4">
              <li>
                <Link href="/docs" className="text-brand hover:text-[#2a6bb0] font-medium">
                  Docs & Guides
                </Link>
              </li>
              <li>
                <div className="flex items-center">
                  <svg className="shrink-0 h-5 w-5 text-gray-300" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                    <path d="M5.555 17.776l8-16 .894.448-8 16-.894-.448z" />
                  </svg>
                  <span className="ml-4 text-sm font-medium text-gray-600">Terms & Conditions</span>
                </div>
              </li>
            </ol>
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="lg:grid lg:grid-cols-4 lg:gap-12">

          {/* Table of Contents - Sidebar */}
          <div className="hidden lg:block lg:col-span-1">
            <div className="sticky top-8">
              <h3 className="text-sm font-semibold tracking-wide uppercase text-gray-400 mb-4">On this page</h3>
              <nav className="space-y-3 border-l-2 border-gray-100 pl-4">
                <a href="#acceptance" className="block text-sm text-gray-600 hover:text-brand transition-colors">Acceptance</a>
                <a href="#definitions" className="block text-sm text-gray-600 hover:text-brand transition-colors">Definitions</a>
                <a href="#service-description" className="block text-sm text-gray-600 hover:text-brand transition-colors">Service Description</a>
                <a href="#accounts" className="block text-sm text-gray-600 hover:text-brand transition-colors">Accounts</a>
                <a href="#acceptable-use" className="block text-sm text-gray-600 hover:text-brand transition-colors">Acceptable Use</a>
                <a href="#sms-terms" className="block text-sm text-gray-600 hover:text-brand transition-colors">SMS Program Terms</a>
                <a href="#agency-responsibilities" className="block text-sm text-gray-600 hover:text-brand transition-colors">Agency Responsibilities</a>
                <a href="#billing" className="block text-sm text-gray-600 hover:text-brand transition-colors">Billing & Payment</a>
                <a href="#ip" className="block text-sm text-gray-600 hover:text-brand transition-colors">Intellectual Property</a>
                <a href="#privacy" className="block text-sm text-gray-600 hover:text-brand transition-colors">Privacy</a>
                <a href="#disclaimers" className="block text-sm text-gray-600 hover:text-brand transition-colors">Disclaimers</a>
                <a href="#limitation" className="block text-sm text-gray-600 hover:text-brand transition-colors">Limitation of Liability</a>
                <a href="#indemnification" className="block text-sm text-gray-600 hover:text-brand transition-colors">Indemnification</a>
                <a href="#termination" className="block text-sm text-gray-600 hover:text-brand transition-colors">Termination</a>
                <a href="#governing-law" className="block text-sm text-gray-600 hover:text-brand transition-colors">Governing Law</a>
                <a href="#changes" className="block text-sm text-gray-600 hover:text-brand transition-colors">Changes</a>
                <a href="#contact" className="block text-sm text-gray-600 hover:text-brand transition-colors">Contact</a>
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms & Conditions</h1>
              <p className="text-xl text-gray-600">
                The terms governing your use of the Voley platform, including our SMS Messaging Program Terms.
              </p>
              <div className="mt-4 flex items-center text-sm text-gray-500">
                <span>Effective Date: [EFFECTIVE_DATE]</span>
                <span className="mx-3">|</span>
                <span>Last Updated: [LAST_UPDATED_DATE]</span>
              </div>
            </div>

            {/* Content Sections */}
            <div className="prose prose-lg max-w-none">

              {/* 1. Acceptance */}
              <section id="acceptance" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">1. Acceptance of Terms</h2>
                <p className="text-gray-600 mb-4">
                  These Terms and Conditions (&quot;Terms&quot;) constitute a legally binding agreement between you (&quot;Customer,&quot; &quot;you,&quot; or &quot;your&quot;) and Voley (&quot;Voley,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) governing your access to and use of the Voley platform at <a href="https://letsvoley.com" className="text-brand hover:text-[#2a6bb0]">letsvoley.com</a> and all related services (collectively, the &quot;Service&quot;).
                </p>
                <p className="text-gray-600 mb-4">
                  By creating an account, accessing the Service, or using any part of the platform, you agree to be bound by these Terms and our <Link href="/docs/privacy-policy" className="text-brand hover:text-[#2a6bb0]">Privacy Policy</Link>. If you are accepting these Terms on behalf of an organization, you represent and warrant that you have the authority to bind that organization.
                </p>
                <p className="text-gray-600 mb-4">
                  If you do not agree to these Terms, do not use the Service.
                </p>
              </section>

              {/* 2. Definitions */}
              <section id="definitions" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">2. Definitions</h2>
                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li><strong>&quot;Customer&quot;</strong> or <strong>&quot;Agency&quot;</strong> means the business entity (typically an insurance agency) that creates an account and uses the Service.</li>
                  <li><strong>&quot;Authorized User&quot;</strong> means an individual granted access to the Service by a Customer (e.g., agency employees and staff).</li>
                  <li><strong>&quot;End User&quot;</strong> means a person who sends or receives messages through the Service as a client, prospect, or contact of the Customer.</li>
                  <li><strong>&quot;Platform&quot;</strong> means the Voley web application, mobile application, APIs, and all related technology and services.</li>
                  <li><strong>&quot;Content&quot;</strong> means all data, text, messages, files, images, and other materials uploaded, transmitted, or generated through the Service.</li>
                </ul>
              </section>

              {/* 3. Service Description */}
              <section id="service-description" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">3. Service Description</h2>
                <p className="text-gray-600 mb-4">
                  Voley provides a business messaging platform that enables Customers to:
                </p>
                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li>Send and receive SMS text messages with their clients and prospects</li>
                  <li>Manage contacts and conversations</li>
                  <li>Collaborate with team members on client communication</li>
                  <li>Integrate with third-party systems (e.g., agency management systems)</li>
                  <li>Register for and maintain A2P 10DLC carrier compliance</li>
                </ul>
                <p className="text-gray-600 mb-4">
                  Voley uses Twilio, a third-party telecommunications provider, to route and deliver SMS messages. Message delivery is subject to carrier network availability, carrier filtering policies, and Twilio&apos;s terms of service.
                </p>
              </section>

              {/* 4. Accounts */}
              <section id="accounts" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">4. Account Registration and Security</h2>
                <p className="text-gray-600 mb-4">
                  To use the Service, you must create an account and provide accurate, complete information. You agree to:
                </p>
                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li>Provide truthful and current registration information</li>
                  <li>Maintain the security of your account credentials</li>
                  <li>Promptly notify us of any unauthorized access to your account</li>
                  <li>Accept responsibility for all activity that occurs under your account</li>
                  <li>Ensure that Authorized Users comply with these Terms</li>
                </ul>
                <p className="text-gray-600 mb-4">
                  We reserve the right to suspend or terminate accounts that provide false information or violate these Terms.
                </p>
              </section>

              {/* 5. Acceptable Use */}
              <section id="acceptable-use" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">5. Acceptable Use</h2>
                <p className="text-gray-600 mb-4">
                  You agree to use the Service only for lawful purposes and in accordance with these Terms. You agree <strong>not</strong> to:
                </p>
                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li>Send unsolicited messages (spam) or messages to recipients who have not consented</li>
                  <li>Send messages that violate the Telephone Consumer Protection Act (TCPA), CAN-SPAM Act, or any other applicable law</li>
                  <li>Send messages containing illegal, threatening, harassing, defamatory, or fraudulent content</li>
                  <li>Send messages containing SHAFT content (Sex, Hate, Alcohol, Firearms, Tobacco) in violation of carrier policies</li>
                  <li>Attempt to circumvent opt-out requests or suppress opt-out records</li>
                  <li>Use the Service to send messages unrelated to your registered use case (insurance agency communications)</li>
                  <li>Reverse engineer, decompile, or attempt to extract the source code of the Platform</li>
                  <li>Interfere with or disrupt the Service or its infrastructure</li>
                  <li>Resell, sublicense, or provide the Service to third parties without authorization</li>
                  <li>Use the Service to transmit malware, viruses, or other harmful code</li>
                </ul>
                <p className="text-gray-600 mb-4">
                  Violation of this section may result in immediate suspension or termination of your account.
                </p>
              </section>

              {/* 6. SMS Program Terms - THE KEY SECTION */}
              <section id="sms-terms" className="mb-12">
                <div className="bg-brand/10 border-2 border-brand/30 rounded-2xl p-8 mb-6">
                  <h2 className="text-2xl font-semibold mb-4 text-gray-900">6. Voley SMS Messaging Program Terms</h2>
                  <p className="text-gray-600 mb-4">
                    This section describes the terms specific to the <strong>Voley SMS Messaging Program</strong> (&quot;SMS Program&quot;). The SMS Program enables Customers (insurance agencies and other businesses) to send and receive text messages with their End Users through the Voley platform using registered 10DLC phone numbers.
                  </p>

                  <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-900">Program Description</h3>
                  <p className="text-gray-600 mb-4">
                    The Voley SMS Messaging Program facilitates business-to-consumer text messaging for legitimate business communications, including but not limited to: insurance policy updates, appointment reminders, claims status notifications, payment reminders, document requests, customer support conversations, and other transactional and informational messages related to the Customer&apos;s services.
                  </p>

                  <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-900">Consent and Opt-In</h3>
                  <p className="text-gray-600 mb-4">
                    End Users are enrolled in the SMS Program only when they provide consent to receive text messages from the Customer (Agency). Consent may be collected by the Agency through various methods, including:
                  </p>
                  <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                    <li>Written or electronic forms (intake forms, applications, enrollment forms)</li>
                    <li>Online forms or web-based consent flows</li>
                    <li>Initiating contact by texting the Agency&apos;s phone number</li>
                  </ul>
                  <p className="text-gray-600 mb-4">
                    By consenting to receive messages, End Users agree to receive text messages from the Customer through the Voley platform at the phone number provided. <strong>Consent to receive text messages is not a condition of purchasing any goods or services.</strong>
                  </p>

                  <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-900">Message Frequency and Rates</h3>
                  <div className="bg-white border border-gray-200 rounded-xl p-5 mb-4">
                    <p className="text-gray-700 mb-2">
                      <strong>Message frequency varies.</strong> The number of messages you receive depends on the nature of your interaction with the Agency and the services they provide.
                    </p>
                    <p className="text-gray-700 mb-0">
                      <strong>Message and data rates may apply.</strong> Charges from your wireless carrier may apply to messages sent and received. Contact your carrier for details about your messaging plan.
                    </p>
                  </div>

                  <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-900">Opt-In &amp; Opt-Out</h3>
                  <div className="bg-white border border-gray-200 rounded-xl p-5 mb-4">
                    <p className="text-gray-700 mb-3">
                      <strong>Opt-In Confirmation Message:</strong> {'{AgencyName}'}: You are subscribed to receive SMS updates regarding your insurance inquiry, policy, and service requests. Msg frequency varies. Msg &amp; data rates may apply. Reply STOP to opt out. Reply HELP for help.
                    </p>
                    <p className="text-gray-700 mb-0">
                      <strong>Opt-Out:</strong> Users may opt out at any time by replying STOP.
                    </p>
                  </div>
                  <p className="text-gray-600 mb-4">
                    Opting out of text messages does not terminate your relationship with the Agency. The Agency may continue to contact you through other channels (phone, email, mail) as permitted by law.
                  </p>

                  <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-900">Help</h3>
                  <div className="bg-white border border-gray-200 rounded-xl p-5 mb-4">
                    <p className="text-gray-700 mb-0">
                      <strong>Reply HELP for help.</strong> You can reply HELP to any message to receive support information, including how to opt out and how to contact the Agency or Voley support.
                    </p>
                  </div>

                  <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-900">Support</h3>
                  <p className="text-gray-600 mb-4">
                    For questions about the SMS Program, your messages, or your privacy:
                  </p>
                  <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                    <li>Contact the Agency that sent you the message (their contact information is typically included in the messages you receive)</li>
                    <li>Contact Voley support at <a href="mailto:support@voley.us" className="text-brand hover:text-[#2a6bb0]">support@voley.us</a></li>
                    <li>Call Voley support at (530) 826-6866</li>
                  </ul>

                  <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-900">Carriers</h3>
                  <p className="text-gray-600 mb-4">
                    The SMS Program is supported on all major US mobile carriers. Carriers are not liable for delayed or undelivered messages.
                  </p>

                  <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-900">Privacy</h3>
                  <p className="text-gray-600 mb-4">
                    Information collected through the SMS Program is governed by our <Link href="/docs/privacy-policy" className="text-brand hover:text-[#2a6bb0]">Privacy Policy</Link>. We do not sell personal information, including phone numbers or message content, to third parties for marketing purposes. Information shared with Twilio for message delivery is governed by Twilio&apos;s privacy policy and our data processing agreements.
                  </p>
                </div>
              </section>

              {/* 7. Agency Responsibilities */}
              <section id="agency-responsibilities" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">7. Agency / Customer Responsibilities</h2>
                <p className="text-gray-600 mb-4">
                  As a Customer using the Service, you are responsible for:
                </p>

                <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-900">Compliance</h3>
                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li>Complying with all applicable federal, state, and local laws, including the TCPA, CAN-SPAM Act, state telemarketing laws, and state privacy laws</li>
                  <li>Obtaining and documenting proper consent from End Users before sending messages through the Service</li>
                  <li>Maintaining records of consent sufficient to demonstrate compliance</li>
                  <li>Checking the National Do Not Call Registry and applicable state DNC lists before sending marketing messages</li>
                  <li>Including required disclosures in consent forms (including that consent is not a condition of purchase)</li>
                </ul>

                <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-900">Content and Conduct</h3>
                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li>Ensuring all message content complies with applicable laws and carrier acceptable use policies</li>
                  <li>Sending messages only within the scope of your A2P 10DLC registered use case</li>
                  <li>Identifying your agency in messages so recipients know who is contacting them</li>
                  <li>Not sending sensitive information (PHI, SSNs, financial account numbers) via SMS</li>
                  <li>Supervising Authorized Users and ensuring they follow these Terms and your internal policies</li>
                </ul>

                <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-900">Data</h3>
                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li>Ensuring the accuracy of contact information and other data you upload to the Platform</li>
                  <li>Providing your own privacy notices to End Users that disclose the use of Voley as a messaging platform</li>
                  <li>Responding to End User data rights requests and coordinating with Voley as needed</li>
                  <li>Maintaining your own backup copies of critical data</li>
                </ul>

                <div className="bg-brand-yellow/15 border border-brand-yellow/40 rounded-2xl p-5 mb-6 [&>:first-child]:mt-0">
                  <p className="text-sm text-gray-700 mb-0">
                    <strong>Voley provides compliance tools</strong> (opt-out handling, message logging, consent tracking) to assist you, but these tools do not transfer legal responsibility. You remain responsible for your compliance obligations.
                  </p>
                </div>
              </section>

              {/* 8. Billing */}
              <section id="billing" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">8. Billing and Payment</h2>
                <p className="text-gray-600 mb-4">
                  You agree to pay all fees associated with your use of the Service in accordance with the pricing plan you selected. Fees may include subscription charges, per-message fees, phone number fees, and other usage-based charges.
                </p>
                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li>Fees are billed in advance or in arrears depending on the charge type, as described in your plan</li>
                  <li>All fees are non-refundable except as expressly stated in these Terms or required by law</li>
                  <li>We may change pricing with [30] days&apos; prior notice; continued use constitutes acceptance</li>
                  <li>Failure to pay may result in suspension or termination of your account</li>
                  <li>You are responsible for all applicable taxes</li>
                </ul>
              </section>

              {/* 9. IP */}
              <section id="ip" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">9. Intellectual Property</h2>

                <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-900">Voley&apos;s Intellectual Property</h3>
                <p className="text-gray-600 mb-4">
                  The Service, including its design, features, code, documentation, trademarks, and all related intellectual property, is owned by Voley and its licensors. These Terms do not grant you any ownership rights in the Service. You are granted a limited, non-exclusive, non-transferable, revocable license to use the Service for your internal business purposes during the term of your subscription.
                </p>

                <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-900">Your Content</h3>
                <p className="text-gray-600 mb-4">
                  You retain ownership of all Content you upload to or transmit through the Service. By using the Service, you grant Voley a limited license to use, store, and transmit your Content solely to provide the Service to you. We will not use your Content for any purpose other than providing, maintaining, and improving the Service.
                </p>
              </section>

              {/* 10. Privacy */}
              <section id="privacy" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">10. Privacy</h2>
                <p className="text-gray-600 mb-4">
                  Your use of the Service is also governed by our <Link href="/docs/privacy-policy" className="text-brand hover:text-[#2a6bb0]">Privacy Policy</Link>, which describes how we collect, use, and protect information. The Privacy Policy is incorporated into these Terms by reference.
                </p>
                <p className="text-gray-600 mb-4">
                  Where we process End User personal information on your behalf, we do so as a Service Provider / Data Processor in accordance with your instructions and applicable data protection law. We will enter into a Data Processing Agreement upon request.
                </p>
              </section>

              {/* 11. Disclaimers */}
              <section id="disclaimers" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">11. Disclaimers</h2>
                <p className="text-gray-600 mb-4 uppercase text-sm font-semibold">
                  THE SERVICE IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
                </p>
                <p className="text-gray-600 mb-4">
                  Without limiting the foregoing, Voley does not warrant that:
                </p>
                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li>The Service will be uninterrupted, error-free, or completely secure</li>
                  <li>All messages will be delivered successfully (delivery depends on carrier networks, recipient devices, and other factors outside our control)</li>
                  <li>The Service will meet all of your requirements</li>
                  <li>Any defects will be corrected within a specific timeframe</li>
                </ul>
                <p className="text-gray-600 mb-4">
                  Voley is not a law firm and does not provide legal advice. Compliance guides, templates, and tools provided through the Service are for informational purposes only and do not constitute legal counsel.
                </p>
              </section>

              {/* 12. Limitation */}
              <section id="limitation" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">12. Limitation of Liability</h2>
                <p className="text-gray-600 mb-4 uppercase text-sm font-semibold">
                  TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, VOLEY SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUE, DATA, OR USE, ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICE, REGARDLESS OF THE THEORY OF LIABILITY.
                </p>
                <p className="text-gray-600 mb-4 uppercase text-sm font-semibold">
                  VOLEY&apos;S TOTAL AGGREGATE LIABILITY FOR ALL CLAIMS ARISING OUT OF OR RELATED TO THESE TERMS OR THE SERVICE SHALL NOT EXCEED THE TOTAL FEES PAID BY YOU TO VOLEY IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.
                </p>
                <p className="text-gray-600 mb-4">
                  Some jurisdictions do not allow the exclusion or limitation of certain damages. In such jurisdictions, our liability is limited to the maximum extent permitted by law.
                </p>
              </section>

              {/* 13. Indemnification */}
              <section id="indemnification" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">13. Indemnification</h2>
                <p className="text-gray-600 mb-4">
                  You agree to indemnify, defend, and hold harmless Voley, its officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, costs, and expenses (including reasonable attorney&apos;s fees) arising out of or related to:
                </p>
                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li>Your use of the Service</li>
                  <li>Your violation of these Terms</li>
                  <li>Your violation of any applicable law, regulation, or third-party right, including TCPA violations resulting from messages you send through the Service</li>
                  <li>Content you upload, transmit, or make available through the Service</li>
                  <li>Your failure to obtain proper consent from End Users</li>
                </ul>
              </section>

              {/* 14. Termination */}
              <section id="termination" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">14. Termination</h2>
                <p className="text-gray-600 mb-4">
                  Either party may terminate the Service:
                </p>
                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li><strong>By Customer:</strong> You may cancel your account at any time by contacting us at <a href="mailto:[SUPPORT_EMAIL]" className="text-brand hover:text-[#2a6bb0]">[SUPPORT_EMAIL]</a>. Cancellation takes effect at the end of your current billing period.</li>
                  <li><strong>By Voley:</strong> We may suspend or terminate your account immediately if you materially breach these Terms, engage in prohibited conduct, fail to pay fees, or if continued service would violate applicable law or carrier requirements.</li>
                </ul>
                <p className="text-gray-600 mb-4">
                  Upon termination, your right to use the Service ceases immediately. We will retain your data for a reasonable period to allow you to export it, after which it will be deleted in accordance with our <Link href="/docs/privacy-policy" className="text-brand hover:text-[#2a6bb0]">Privacy Policy</Link>. Sections that by their nature should survive termination (including Disclaimers, Limitation of Liability, Indemnification, and Governing Law) will survive.
                </p>
              </section>

              {/* 15. Governing Law */}
              <section id="governing-law" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">15. Governing Law and Dispute Resolution</h2>
                <p className="text-gray-600 mb-4">
                  These Terms are governed by and construed in accordance with the laws of the State of [STATE], without regard to its conflict of law principles. Any dispute arising out of or relating to these Terms or the Service shall be resolved in the state or federal courts located in [COUNTY], [STATE], and you consent to the personal jurisdiction of such courts.
                </p>
                <p className="text-gray-600 mb-4">
                  Before initiating any formal proceedings, the parties agree to attempt to resolve disputes through good-faith negotiation for a period of at least [30] days.
                </p>
              </section>

              {/* 16. Changes */}
              <section id="changes" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">16. Changes to These Terms</h2>
                <p className="text-gray-600 mb-4">
                  We may modify these Terms from time to time. We will provide notice of material changes through the Platform or by email at least [30] days before the changes take effect. Your continued use of the Service after the effective date constitutes acceptance of the updated Terms. If you do not agree with the changes, you must stop using the Service and cancel your account before the effective date.
                </p>
              </section>

              {/* 17. Miscellaneous */}
              <section className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">17. General Provisions</h2>
                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li><strong>Entire Agreement:</strong> These Terms, together with the Privacy Policy, constitute the entire agreement between you and Voley regarding the Service.</li>
                  <li><strong>Severability:</strong> If any provision of these Terms is found to be unenforceable, the remaining provisions will remain in full effect.</li>
                  <li><strong>Waiver:</strong> Failure to enforce any provision does not constitute a waiver of that provision or the right to enforce it later.</li>
                  <li><strong>Assignment:</strong> You may not assign these Terms without our prior written consent. Voley may assign these Terms in connection with a merger, acquisition, or sale of assets.</li>
                  <li><strong>Force Majeure:</strong> Voley is not liable for delays or failures caused by events beyond our reasonable control, including carrier outages, natural disasters, or government actions.</li>
                  <li><strong>Notices:</strong> Notices to you will be sent to the email address associated with your account. Notices to Voley should be sent to <a href="mailto:[SUPPORT_EMAIL]" className="text-brand hover:text-[#2a6bb0]">[SUPPORT_EMAIL]</a>.</li>
                </ul>
              </section>

              {/* 18. Contact */}
              <section id="contact" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">18. Contact Us</h2>
                <p className="text-gray-600 mb-4">
                  If you have questions about these Terms, please contact us:
                </p>
                <div className="bg-white border border-gray-200 rounded-2xl px-5 py-4 mb-6 [&>:first-child]:mt-0">
                  <p className="text-gray-700 mb-2"><strong>Voley</strong></p>
                  <p className="text-gray-600 mb-1">Email: <a href="mailto:support@voley.us" className="text-brand hover:text-[#2a6bb0]">support@voley.us</a></p>
                  <p className="text-gray-600 mb-1">Phone: (530) 826-6866</p>
                  <p className="text-gray-600 mb-1">Address: 7969 Meyer Vista Cove, Sandy, UT 84093</p>
                  <p className="text-gray-600 mb-0">Website: <a href="https://letsvoley.com" className="text-brand hover:text-[#2a6bb0]">letsvoley.com</a></p>
                </div>
              </section>

            </div>

            {/* Footer Navigation */}
            <div className="mt-16 pt-8 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <Link
                  href="/docs"
                  className="inline-flex items-center gap-2 px-5 py-2.5 border border-gray-200 text-sm font-medium rounded-full text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-300 hover:shadow-sm transition-all group"
                >
                  <ChevronLeft className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" />
                  Back to Docs
                </Link>
                <div className="text-sm text-gray-500">
                  See also: <Link href="/docs/privacy-policy" className="text-brand hover:text-[#2a6bb0] font-medium">Privacy Policy</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
